import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { api, ApiError } from '../api.js';
import LanguageToggle from '../components/LanguageToggle.js';
import logoUrl from '../assets/logo.png';
import styles from './GuestGateScreen.module.css';

/**
 * Where Google sends a guest who decided to register (PLAN.md Phase 10.29).
 *
 * For the two seconds this screen is on the page, the browser holds both
 * credentials at once — the guest cookie for the identity being claimed, and a
 * fresh account session for the account claiming it — which is exactly what
 * `POST /api/guest/upgrade` requires. It runs that once and leaves.
 *
 * Nothing is lost in the move: the photo, the circle membership, the bolões
 * joined and every prediction already made keep their rows and simply point at
 * the new account. What changes is what the person can do next — see other
 * circles, be invited to more, start their own.
 *
 * Deliberately not guarded by `RequireActor`: at this instant the visitor
 * belongs to neither guard cleanly, and bouncing them would strand a
 * half-finished upgrade.
 */
export default function UpgradeScreen() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [error, setError] = useState<ApiError | null>(null);

  // Runs exactly once even under React's development double-invoke: the
  // second call would find no guest session left and report a confusing
  // failure over a merge that actually succeeded.
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;

    void (async () => {
      try {
        const { groupId } = await api.upgradeGuest();
        await queryClient.invalidateQueries();
        void navigate(`/groups/${groupId}`, { replace: true });
      } catch (caught) {
        setError(caught instanceof ApiError ? caught : null);
      }
    })();
  }, [navigate, queryClient]);

  if (!error) {
    return (
      <main className={styles.screen}>
        <div className={styles.brand}>
          <img className={styles.logo} src={logoUrl} alt="" />
        </div>
        <p className="muted" style={{ textAlign: 'center' }}>
          {t('guest.upgrading')}
        </p>
      </main>
    );
  }

  return (
    <main className={styles.screen}>
      <div className={styles.topBar}>
        <LanguageToggle />
      </div>
      <div className={`panel ${styles.card}`}>
        <h1 className={styles.title}>{t('guest.upgradeFailedTitle')}</h1>
        <p className="muted">
          {error.code === 'already_member'
            ? t('guest.upgradeAlreadyMember')
            : error.code === 'not_a_guest_session'
              ? t('guest.upgradeNoGuest')
              : t('common.somethingWentWrong')}
        </p>
        <button
          type="button"
          className="button button-primary"
          onClick={() => void navigate('/', { replace: true })}
        >
          {t('common.back')}
        </button>
      </div>
    </main>
  );
}
